/**
 * chainSigners — Client-side transaction signing
 * 
 * Uses Ed25519 (via nacl/tweetnacl) for signing in mock mode.
 * When switching to real chain mode, plug in chain-specific libraries here.
 * 
 * The signing flow:
 *   1. Server returns tx_blob (base64) from /prepare endpoint
 *   2. Client decodes tx_blob and signs with private key
 *   3. Client sends signed_tx + signature to /broadcast endpoint
 *   4. Server verifies signature against stored public key
 * 
 * Usage:
 *   import { signTransaction } from '@/crypto/chainSigners';
 *   const { signedTx, signature } = await signTransaction(txBlob, privateKeyHex, chain);
 */

/**
 * Convert hex string to Uint8Array
 */
function hexToBytes(hex) {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
  }
  return bytes;
}

/**
 * Convert Uint8Array to base64
 */
function bytesToBase64(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Convert base64 to Uint8Array
 */
function base64ToBytes(b64) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Ed25519 sign using Web Crypto API (fallback to pure-JS if unavailable)
 * 
 * Web Crypto Ed25519 support is available in modern browsers (Chrome 113+, Firefox, Safari 17+).
 * Falls back to a HMAC-SHA256 based signature for older browsers.
 */
async function ed25519Sign(message, privateKeyHex) {
  const privateKeyBytes = hexToBytes(privateKeyHex);
  
  // Try Web Crypto Ed25519 first
  try {
    // Ed25519 private key in PKCS8 format for Web Crypto
    // The raw 32-byte seed needs to be wrapped in PKCS8 DER
    const pkcs8Prefix = new Uint8Array([
      0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06,
      0x03, 0x2b, 0x65, 0x70, 0x04, 0x22, 0x04, 0x20
    ]);
    const pkcs8Key = new Uint8Array(pkcs8Prefix.length + 32);
    pkcs8Key.set(pkcs8Prefix);
    pkcs8Key.set(privateKeyBytes.slice(0, 32), pkcs8Prefix.length);

    const cryptoKey = await crypto.subtle.importKey(
      'pkcs8', pkcs8Key, { name: 'Ed25519' }, false, ['sign']
    );

    const signature = await crypto.subtle.sign('Ed25519', cryptoKey, message);
    return new Uint8Array(signature);
  } catch (e) {
    // Web Crypto Ed25519 not supported — use HMAC-SHA256 fallback
    // This produces a deterministic signature the server can verify via
    // the same fallback path using PyNaCl / HMAC
    console.warn('[chainSigners] Ed25519 Web Crypto unavailable, using HMAC fallback:', e.message);
    return await hmacFallbackSign(message, privateKeyBytes);
  }
}

/**
 * HMAC-SHA256 fallback for browsers without Ed25519 Web Crypto support.
 * Server must also accept HMAC signatures in this case.
 */
async function hmacFallbackSign(message, keyBytes) {
  const cryptoKey = await crypto.subtle.importKey(
    'raw', keyBytes.slice(0, 32),
    { name: 'HMAC', hash: 'SHA-256' },
    false, ['sign']
  );
  const sig = await crypto.subtle.sign('HMAC', cryptoKey, message);
  return new Uint8Array(sig);
}


/**
 * Sign a transaction blob.
 * 
 * @param {string} txBlobBase64 - Base64-encoded transaction blob from /prepare
 * @param {string} privateKeyHex - Private key hex from KeyVault
 * @param {string} chain - Chain identifier (for future chain-specific signing)
 * @returns {Object} { signedTx: string, signature: string } - Both base64 encoded
 */
export async function signTransaction(txBlobBase64, privateKeyHex, chain) {
  if (!txBlobBase64 || !privateKeyHex) {
    throw new Error('Transaction blob and private key are required');
  }

  // Decode the tx blob
  const txBytes = base64ToBytes(txBlobBase64);
  
  // Sign the raw bytes
  // In the future, switch on `chain` for chain-specific signing:
  //   case 'eth': return signEthereum(txBytes, privateKeyHex);
  //   case 'sol': return signSolana(txBytes, privateKeyHex);
  //   etc.
  const signatureBytes = await ed25519Sign(txBytes, privateKeyHex);

  return {
    signedTx: txBlobBase64,  // In mock mode, signed_tx = original tx blob
    signature: bytesToBase64(signatureBytes)
  };
}


/**
 * Sign a vault operation (deposit or withdraw).
 * Same as signTransaction but semantically distinct for clarity.
 */
export async function signVaultOperation(txBlobBase64, privateKeyHex, chain) {
  return signTransaction(txBlobBase64, privateKeyHex, chain);
}


/**
 * Verify a signature locally (for debugging / UI feedback).
 * In production, the server does the authoritative verification.
 */
export async function verifySignatureLocal(message, signatureBase64, publicKeyHex) {
  try {
    const msgBytes = typeof message === 'string' ? base64ToBytes(message) : message;
    const sigBytes = base64ToBytes(signatureBase64);
    const pubKeyBytes = hexToBytes(publicKeyHex);

    // Try Web Crypto Ed25519 verification
    const spkiPrefix = new Uint8Array([
      0x30, 0x2a, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65,
      0x70, 0x03, 0x21, 0x00
    ]);
    const spkiKey = new Uint8Array(spkiPrefix.length + 32);
    spkiKey.set(spkiPrefix);
    spkiKey.set(pubKeyBytes.slice(0, 32), spkiPrefix.length);

    const cryptoKey = await crypto.subtle.importKey(
      'spki', spkiKey, { name: 'Ed25519' }, false, ['verify']
    );

    return await crypto.subtle.verify('Ed25519', cryptoKey, sigBytes, msgBytes);
  } catch {
    // Can't verify locally — server will handle it
    return null;
  }
}
